import { Link } from "@tanstack/react-router";
import { type Constraints, type RuleGroup, constraintSummary, describeGroup, durationLabel } from "@trackmark/core";
import { RankTable, rankTableStyles as table } from "../design/RankTable.js";
import { RatingStrip } from "../design/RatingStrip.js";
import styles from "./Playlist.module.css";

/**
 * One saved playlist.
 *
 * A playlist is a **snapshot**: a decision about which songs, taken on a date. That is the whole
 * difference between it and a dynamic list, and it decides everything on this screen —
 * `Regenerate` makes a NEW playlist rather than refreshing this one, and the track list never
 * silently changes under you.
 *
 * The recipe travels with it, because the first thing you want on opening one is to know what it
 * was asking for, and the second is to run it again. A track list with no recipe beside it cannot
 * be read, argued with, or repeated.
 */

export interface PlaylistTrack {
  position: number;
  id: number;
  title: string;
  artist: string;
  artistId: number;
  album: string;
  albumId: number;
  year: number | null;
  durationMs: number | null;
  /** Read LIVE. The track list is the snapshot; a rating is a fact about a song now. */
  score: number | null;
  art: string | null;
}

export interface PlaylistDetail {
  id: number;
  name: string;
  rules: RuleGroup;
  constraints: Constraints;
  targetCount: number | null;
  createdAt: string;
  durationMs: number;
  tracks: PlaylistTrack[];
}

export interface PlaylistProps {
  playlist: PlaylistDetail;
  onDelete: () => void;
  /** Takes the recipe to the generator. Never mutates this playlist. */
  onRegenerate: () => void;
}

const COLUMNS = "40px 24px 1fr 200px 120px 42px 56px";

const dateLabel = (iso: string) => {
  const d = new Date(iso);
  const M = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];
  return `${String(d.getUTCDate()).padStart(2, "0")} ${M[d.getUTCMonth()]} ${String(d.getUTCFullYear() % 100).padStart(2, "0")}`;
};

const trackLength = (ms: number | null) =>
  ms === null ? "—" : `${Math.floor(ms / 60000)}:${String(Math.floor((ms % 60000) / 1000)).padStart(2, "0")}`;

export function Playlist({ playlist, onDelete, onRegenerate }: PlaylistProps) {
  const { tracks, targetCount } = playlist;
  const short = targetCount !== null && tracks.length < targetCount;

  return (
    <div className={styles.page}>
      <div className={styles.head}>
        <div className={styles.title} data-testid="playlist-title">
          {playlist.name}
        </div>
        <Link to="/playlists" className={styles.back} data-testid="all-playlists">
          ← All playlists
        </Link>
      </div>

      {/*
        * The origin line. `41 OF 50` is kept rather than smoothed to `41 TRACKS`: the gap between
        * what was asked for and what was picked is the honest part of a playlist, and it was
        * visible on the day it was generated (§J15).
        */}
      <div className={styles.origin} data-testid="playlist-origin">
        GENERATED {dateLabel(playlist.createdAt)}
        <span className={styles.dot}>·</span>
        <span data-short={short || undefined} data-testid="playlist-count">
          {tracks.length}
          {targetCount === null ? " TRACKS" : ` OF ${targetCount} ASKED FOR`}
        </span>
        <span className={styles.dot}>·</span>
        {durationLabel(playlist.durationMs).toUpperCase()}
      </div>

      <div className={styles.recipe} data-testid="playlist-recipe">
        <div className={styles.recipeLabel}>THE RECIPE</div>
        <div className={styles.recipeRules} data-testid="recipe-rules">
          {describeGroup(playlist.rules)}
        </div>
        <div className={styles.recipeConstraints} data-testid="recipe-constraints">
          {constraintSummary(playlist.constraints).join(" · ").toUpperCase()}
        </div>
        <div className={styles.actions}>
          <button type="button" className={styles.primary} data-testid="regenerate" onClick={onRegenerate}>
            Regenerate →
          </button>
          {/* Says what it does, because "Regenerate" beside a saved thing reads as "overwrite". */}
          <span className={styles.actionNote}>MAKES A NEW PLAYLIST · THIS ONE IS A SNAPSHOT AND STAYS AS IT IS</span>
          <button type="button" className={styles.remove} data-testid="delete-playlist" onClick={onDelete}>
            Delete
          </button>
        </div>
      </div>

      {tracks.length === 0 ? (
        <div className={styles.empty} data-testid="playlist-empty">
          <div className={styles.emptyTitle}>Nothing left in this one</div>
          <div className={styles.emptyHint}>
            EVERY SONG IT HELD HAS BEEN REMOVED FROM THE LIBRARY. THE RECIPE ABOVE STILL RUNS.
          </div>
        </div>
      ) : (
        <RankTable
          rows={tracks}
          columns={COLUMNS}
          rowKey={(t) => t.id}
          rowProps={(t) => ({ "data-testid": "playlist-track", "data-state": t.score === null ? "unrated" : undefined })}
          header={
            <>
              <div>#</div>
              <div />
              <div>TITLE</div>
              <div>ARTIST</div>
              <div>RATING</div>
              <div style={{ textAlign: "right" }}>SCR</div>
              <div style={{ textAlign: "right" }}>DUR</div>
            </>
          }
          renderRow={(t) => (
            <>
              <div className={styles.position}>{t.position}</div>
              <Link
                to="/album/$albumId"
                params={{ albumId: String(t.albumId) }}
                aria-label={`Go to ${t.album}`}
                data-testid="track-album-link"
              >
                {t.art ? (
                  <img className={table.art} src={t.art} alt="" />
                ) : (
                  <div className={table.artFallback}>{t.album.slice(0, 8)}</div>
                )}
              </Link>
              <div className={table.title}>{t.title}</div>
              <Link
                to="/artist/$artistId"
                params={{ artistId: String(t.artistId) }}
                className={table.secondaryLink}
              >
                {t.artist}
              </Link>
              <RatingStrip value={t.score} />
              {/* A song rated since the snapshot, or cleared since, shows what it is NOW. */}
              <div className={table.score}>{t.score ?? "—"}</div>
              <div className={table.meta}>{trackLength(t.durationMs)}</div>
            </>
          )}
        />
      )}
    </div>
  );
}
